import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import {
  AuthService,
  AUTH_STORAGE_KEYS,
  AUTH_REFRESH_RETRIED,
  SKIP_INTERCEPTOR,
  authEvents,
  isAuthFatalError,
  shouldRefreshOnHttpError,
} from '@aurrica-web/core-data';
import { injectDispatch } from '@ngrx/signals/events';

function attachBearer(
  req: HttpRequest<unknown>,
  idToken: string | null,
): HttpRequest<unknown> {
  if (!idToken) {
    return req;
  }
  return req.clone({
    setHeaders: { Authorization: `Bearer ${idToken}` },
  });
}

function handleHttpError(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  authDispatch: ReturnType<typeof injectDispatch<typeof authEvents>>,
): Observable<HttpEvent<unknown>> {
  if (req.context.get(AUTH_REFRESH_RETRIED)) {
    authDispatch.logout();
    return throwError(() => error);
  }

  const storedId = localStorage.getItem(AUTH_STORAGE_KEYS.idToken);
  const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);

  if (
    !refreshToken ||
    !shouldRefreshOnHttpError(
      error.status,
      error.error,
      storedId,
      (token) => authService.isTokenValid(token),
    )
  ) {
    return throwError(() => error);
  }

  return authService.ensureFreshSession().pipe(
    switchMap(() => {
      const newIdToken = localStorage.getItem(AUTH_STORAGE_KEYS.idToken);
      const retryRequest = attachBearer(req, newIdToken).clone({
        context: req.context.set(AUTH_REFRESH_RETRIED, true),
      });
      return next(retryRequest);
    }),
    catchError((refreshError) => {
      if (isAuthFatalError(refreshError)) {
        authDispatch.logout();
      }
      return throwError(() => error);
    }),
  );
}

function sendAuthenticatedRequest(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  authDispatch: ReturnType<typeof injectDispatch<typeof authEvents>>,
): Observable<HttpEvent<unknown>> {
  const idToken = localStorage.getItem(AUTH_STORAGE_KEYS.idToken);
  return next(attachBearer(req, idToken)).pipe(
    catchError((error: HttpErrorResponse) =>
      handleHttpError(error, req, next, authService, authDispatch),
    ),
  );
}

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const authDispatch = injectDispatch(authEvents);

  if (req.context.get(SKIP_INTERCEPTOR)) {
    return next(req);
  }

  const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
  if (!refreshToken) {
    return sendAuthenticatedRequest(req, next, authService, authDispatch);
  }

  return authService.ensureFreshSession().pipe(
    switchMap(() => sendAuthenticatedRequest(req, next, authService, authDispatch)),
    catchError((refreshError) => {
      if (isAuthFatalError(refreshError)) {
        authDispatch.logout();
        return throwError(() => refreshError);
      }
      return sendAuthenticatedRequest(req, next, authService, authDispatch);
    }),
  );
}
